// contexts/ThemeContext.js - Version améliorée avec couleurs dynamiques
import React, { createContext, useContext, useEffect } from 'react';
import { useBoutique } from './BoutiqueContext';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper function pour convertir hex en RGB
const hexToRgb = (hex) => {
  if (!hex) return null;
  
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Helper function pour assombrir une couleur
const darkenColor = (color, amount) => {
  if (!color) return color;
  const hex = color.replace('#', '');
  const num = parseInt(hex, 16);
  const amt = Math.round(2.55 * amount * 100);
  const R = (num >> 16) - amt;
  const G = ((num >> 8) & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000
    + (G < 255 ? G < 1 ? 0 : G : 255) * 0x100
    + (B < 255 ? B < 1 ? 0 : B : 255))
    .toString(16).slice(1);
};

export const ThemeProvider = ({ children }) => {
  const { currentBoutique, getBoutiqueTheme } = useBoutique();
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    
    
    if (isSuperAdmin) {
      applyDefaultTheme();
    } else {
      // ✅ UTILISE getBoutiqueTheme du BoutiqueContext
      const boutiqueTheme = getBoutiqueTheme ? getBoutiqueTheme() : null;
      
      if (boutiqueTheme && (boutiqueTheme.primaryColor !== '#667eea' || boutiqueTheme.secondaryColor !== '#764ba2')) {
        applyBoutiqueThemeFromColors(boutiqueTheme);
      } else {
        applyDefaultTheme();
      }
    }
  }, [currentBoutique, isSuperAdmin, getBoutiqueTheme]);

  // ✅ FONCTION pour appliquer le thème boutique
  const applyBoutiqueThemeFromColors = (theme) => {
    const root = document.documentElement;
    
    const primaryColor = theme.primaryColor || '#667eea';
    const secondaryColor = theme.secondaryColor || '#764ba2';
    
    
    // Variables Bootstrap principales
    root.style.setProperty('--bs-primary', primaryColor);
    root.style.setProperty('--bs-secondary', secondaryColor);
    
    // Conversion en RGB pour Bootstrap
    const primaryRgb = hexToRgb(primaryColor);
    const secondaryRgb = hexToRgb(secondaryColor);
    
    if (primaryRgb) {
      root.style.setProperty('--bs-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
      root.style.setProperty('--primary-light', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
      root.style.setProperty('--primary-hover', darkenColor(primaryColor, 0.1));
    }
    
    if (secondaryRgb) {
      root.style.setProperty('--bs-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
      root.style.setProperty('--secondary-light', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.1)`);
      root.style.setProperty('--secondary-hover', darkenColor(secondaryColor, 0.1));
    }
    
    // Variables personnalisées pour les composants
    root.style.setProperty('--sidebar-bg', primaryColor);
    root.style.setProperty('--navbar-bg', secondaryColor);
    root.style.setProperty('--accent-color', primaryColor);
    
    // ✅ NOUVEAU: Variables spécifiques pour le Layout
    root.style.setProperty('--theme-primary', primaryColor);
    root.style.setProperty('--theme-secondary', secondaryColor);
    
    // Gradient pour le Layout
    root.style.setProperty('--primary-gradient', 
      `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`);
    
    // Gestion du logo
    if (theme.logoUrl) {
      root.style.setProperty('--boutique-logo', `url(${theme.logoUrl})`);
    } else {
      root.style.removeProperty('--boutique-logo');
    }
    
  };

  const applyDefaultTheme = () => {
    const root = document.documentElement;
    
    // Couleurs par défaut
    const defaultPrimary = '#667eea';
    const defaultSecondary = '#764ba2';
    
    root.style.setProperty('--bs-primary', defaultPrimary);
    root.style.setProperty('--bs-secondary', defaultSecondary);
    root.style.setProperty('--bs-primary-rgb', '102, 126, 234');
    root.style.setProperty('--bs-secondary-rgb', '118, 75, 162');
    root.style.setProperty('--primary-light', 'rgba(102, 126, 234, 0.1)');
    root.style.setProperty('--secondary-light', 'rgba(118, 75, 162, 0.1)');
    root.style.setProperty('--primary-hover', '#5a67d8');
    root.style.setProperty('--secondary-hover', '#6b46c1');
    root.style.setProperty('--sidebar-bg', defaultPrimary);
    root.style.setProperty('--navbar-bg', defaultSecondary);
    root.style.setProperty('--accent-color', defaultPrimary);
    
    // ✅ NOUVEAU: Variables spécifiques pour le Layout
    root.style.setProperty('--theme-primary', defaultPrimary);
    root.style.setProperty('--theme-secondary', defaultSecondary);
    
    root.style.setProperty('--primary-gradient', 
      `linear-gradient(135deg, ${defaultPrimary} 0%, ${defaultSecondary} 100%)`);
    
    // Supprimer le logo boutique
    root.style.removeProperty('--boutique-logo');
    
  };

  // ✅ FONCTION getCurrentTheme améliorée
  const getCurrentTheme = () => {
    if (isSuperAdmin) {
      return {
        primaryColor: '#667eea',
        secondaryColor: '#764ba2',
        logoUrl: null,
        name: 'SmartBoutik'
      };
    }

    if (getBoutiqueTheme) {
      const theme = getBoutiqueTheme();
      return {
        ...theme,
        name: currentBoutique?.name || 'Ma Boutique'
      };
    }
    
    return {
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      logoUrl: null,
      name: 'Ma Boutique'
    };
  };

  // ✅ FONCTIONS utilitaires pour les composants
  const getButtonClass = (variant = 'primary') => {
    switch (variant) {
      case 'primary':
        return 'btn btn-primary';
      case 'secondary':
        return 'btn btn-secondary';
      case 'outline-primary':
        return 'btn btn-outline-primary';
      case 'outline-secondary':
        return 'btn btn-outline-secondary';
      default:
        return `btn btn-${variant}`;
    }
  };

  const getCardStyle = (elevated = false) => {
    const theme = getCurrentTheme();
    
    return {
      borderLeft: `4px solid ${theme.primaryColor}`,
      boxShadow: elevated ? 
        `0 8px 25px rgba(0,0,0,0.1), 0 0 0 1px ${theme.primaryColor}20` : 
        '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease'
    };
  };

  // ✅ NOUVELLE FONCTION: Obtenir les couleurs pour les avatars des rôles
  const getRoleColors = (role) => {
    const currentTheme = getCurrentTheme();
    
    switch (role) {
      case 'SUPER_ADMIN':
        return {
          color: '#3b82f6', // ✅ Couleur fixe pour Super Admin
          gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)'
        };
      case 'MANAGER':
        return {
          color: currentTheme.primaryColor, // ✅ Couleur dynamique de la boutique
          gradient: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${darkenColor(currentTheme.primaryColor, 0.2)})`
        };
      case 'EMPLOYEE':
        return {
          color: currentTheme.primaryColor, // ✅ Couleur dynamique de la boutique
          gradient: `linear-gradient(135deg, ${currentTheme.primaryColor}, ${darkenColor(currentTheme.primaryColor, 0.2)})`
        };
      default:
        return {
          color: '#6b7280',
          gradient: 'linear-gradient(135deg, #6b7280, #4b5563)'
        };
    }
  };

  // ✅ FONCTION pour obtenir les couleurs CSS dynamiques
  const getCSSVariables = () => {
    const theme = getCurrentTheme();
    return {
      '--theme-primary': theme.primaryColor,
      '--theme-secondary': theme.secondaryColor,
      '--theme-primary-rgb': hexToRgb(theme.primaryColor),
      '--theme-secondary-rgb': hexToRgb(theme.secondaryColor)
    };
  };

  const value = {
    // ✅ PROPRIÉTÉS existantes
    currentTheme: getCurrentTheme(),
    applyBoutiqueThemeFromColors,
    applyDefaultTheme,
    getButtonClass,
    getCardStyle,
    isSuperAdmin,
    
    // ✅ PROPRIÉTÉS directes pour faciliter l'utilisation
    primaryColor: getCurrentTheme().primaryColor,
    secondaryColor: getCurrentTheme().secondaryColor,
    logoUrl: getCurrentTheme().logoUrl,
    
    // ✅ NOUVELLES FONCTIONS
    getRoleColors,
    getCSSVariables,
    
    // ✅ FONCTIONS utilitaires
    hexToRgb,
    darkenColor
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};